import { useState, useCallback, useRef } from "react";
import { VideoPlayer } from "./VideoPlayer";
import { Button } from "@/components/ui/button";
import { CheckCircle2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

interface JourneyVideoStepProps {
  stepId: string;
  userId: string;
  videoUrl: string;
  secondVideoUrl?: string; // Optional second video for multi-video steps like Core Training
  requiredWatchPercent?: number;
  initialProgress?: number;
  initialSecondVideoProgress?: number; // Initial progress for second video
  isCompleted: boolean;
  onComplete: () => void;
  introText?: string;
}

export function JourneyVideoStep({
  stepId,
  userId,
  videoUrl,
  secondVideoUrl,
  requiredWatchPercent = 95,
  initialProgress = 0,
  initialSecondVideoProgress = 0,
  isCompleted,
  onComplete,
  introText,
}: JourneyVideoStepProps) {
  const [watchedPercent, setWatchedPercent] = useState(initialProgress);
  const [secondVideoWatchedPercent, setSecondVideoWatchedPercent] = useState(initialSecondVideoProgress);
  const [canContinue, setCanContinue] = useState(
    initialProgress >= requiredWatchPercent && 
    (!secondVideoUrl || initialSecondVideoProgress >= requiredWatchPercent)
  );
  const [secondVideoComplete, setSecondVideoComplete] = useState(
    !secondVideoUrl || initialSecondVideoProgress >= requiredWatchPercent
  );
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { toast } = useToast();
  
  // Ref to track watched percent for 10% interval saves (avoids stale closure)
  const watchedPercentRef = useRef(initialProgress);
  const secondVideoPercentRef = useRef(initialSecondVideoProgress);

  const handleProgress = useCallback(async (percent: number) => {
    const prevPercent = watchedPercentRef.current;
    watchedPercentRef.current = percent;
    setWatchedPercent(percent);
    
    // Save progress to database every 10%
    if (Math.floor(percent / 10) > Math.floor(prevPercent / 10)) {
      await supabase
        .from('user_journey_step_progress')
        .upsert({
          user_id: userId,
          step_id: stepId,
          status: 'active',
          action_data: { watch_percent: percent, second_video_percent: secondVideoPercentRef.current },
        }, { onConflict: 'user_id,step_id' });
    }
  }, [stepId, userId]);

  const handleSecondVideoProgress = useCallback(async (percent: number) => {
    const prevPercent = secondVideoPercentRef.current;
    secondVideoPercentRef.current = percent;
    setSecondVideoWatchedPercent(percent);
    
    // Save progress to database every 10%
    if (Math.floor(percent / 10) > Math.floor(prevPercent / 10)) {
      await supabase
        .from('user_journey_step_progress')
        .upsert({
          user_id: userId,
          step_id: stepId,
          status: 'active',
          action_data: { watch_percent: watchedPercentRef.current, second_video_percent: percent },
        }, { onConflict: 'user_id,step_id' });
    }
  }, [stepId, userId]);

  const handleVideoComplete = useCallback(() => {
    // Only enable continue when first video is complete AND second video is complete (or doesn't exist)
    if (secondVideoComplete || !secondVideoUrl) {
      setCanContinue(true);
    }
  }, [secondVideoComplete, secondVideoUrl]);

  const handleSecondVideoComplete = useCallback(() => {
    setSecondVideoComplete(true);
    // If first video already complete, enable continue
    if (watchedPercentRef.current >= requiredWatchPercent) {
      setCanContinue(true);
    }
  }, [requiredWatchPercent]);

  const handleContinue = async () => {
    setIsSubmitting(true);
    try {
      // Mark step as completed
      const { error } = await supabase
        .from('user_journey_step_progress')
        .upsert({
          user_id: userId,
          step_id: stepId,
          status: 'completed',
          completed_at: new Date().toISOString(),
          verified_completion: true,
          action_data: { 
            watch_percent: watchedPercentRef.current, 
            second_video_percent: secondVideoPercentRef.current 
          },
        }, { onConflict: 'user_id,step_id' });

      if (error) throw error;

      // Unlock next step
      const { data: currentStep } = await supabase
        .from('journey_steps')
        .select('step_number')
        .eq('id', stepId)
        .single();

      if (currentStep) {
        const { data: nextStep } = await supabase
          .from('journey_steps')
          .select('id')
          .eq('step_number', currentStep.step_number + 1)
          .single();

        if (nextStep) {
          await supabase
            .from('user_journey_step_progress')
            .upsert({
              user_id: userId,
              step_id: nextStep.id,
              status: 'active',
              started_at: new Date().toISOString(),
            }, { onConflict: 'user_id,step_id' });
        } else {
          // No more steps - mark journey complete
          await supabase
            .from('user_journey_state')
            .upsert({
              user_id: userId,
              journey_completed: true,
              journey_completed_at: new Date().toISOString(),
            }, { onConflict: 'user_id' });
        }
      }

      onComplete();
    } catch (error) {
      console.error('Error completing step:', error);
      toast({
        title: "Error",
        description: "Failed to save progress. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isCompleted) {
    return (
      <div className="space-y-6">
        {introText && (
          <p className="text-muted-foreground">{introText}</p>
        )}
        <div className="relative">
          <VideoPlayer
            videoUrl={videoUrl}
            requiredWatchPercent={0}
            onProgress={() => {}}
            onComplete={() => {}}
            initialProgress={0}
            hideProgressUI
          />
          <div className="absolute top-2 right-2 sm:top-3 sm:right-3 bg-green-600/90 text-white px-2 py-0.5 sm:px-3 sm:py-1 rounded-full flex items-center gap-1 sm:gap-1.5 text-xs sm:text-sm font-medium shadow-lg pointer-events-none">
            <CheckCircle2 className="w-3 h-3 sm:w-4 sm:h-4" />
            <span>Completed</span>
          </div>
        </div>
        {secondVideoUrl && (
          <div className="relative">
            <VideoPlayer
              videoUrl={secondVideoUrl}
              requiredWatchPercent={0}
              onProgress={() => {}}
              onComplete={() => {}}
              initialProgress={0}
              hideProgressUI
            />
            <div className="absolute top-2 right-2 sm:top-3 sm:right-3 bg-green-600/90 text-white px-2 py-0.5 sm:px-3 sm:py-1 rounded-full flex items-center gap-1 sm:gap-1.5 text-xs sm:text-sm font-medium shadow-lg pointer-events-none">
              <CheckCircle2 className="w-3 h-3 sm:w-4 sm:h-4" />
              <span>Completed</span>
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {introText && (
        <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <p className="text-sm text-blue-800">{introText}</p>
        </div>
      )}
      <VideoPlayer
        videoUrl={videoUrl}
        requiredWatchPercent={requiredWatchPercent}
        onProgress={handleProgress}
        onComplete={handleVideoComplete}
        initialProgress={initialProgress}
      />

      {secondVideoUrl && (
        <div className="mt-6">
          <p className="text-sm font-medium mb-3 text-muted-foreground">Additional Training Video:</p>
          <VideoPlayer
            videoUrl={secondVideoUrl}
            requiredWatchPercent={requiredWatchPercent}
            onProgress={handleSecondVideoProgress}
            onComplete={handleSecondVideoComplete}
            initialProgress={initialSecondVideoProgress}
          />
        </div>
      )}

      {canContinue && secondVideoComplete && (
        <div className="flex justify-end">
          <Button 
            onClick={handleContinue} 
            disabled={isSubmitting}
            size="lg"
          >
            {isSubmitting ? "Saving..." : "Acknowledge and Continue"}
          </Button>
        </div>
      )}
    </div>
  );
}
